import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import style from './index.module.less';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { AutoComplete, Avatar, Breadcrumb, Button, Dropdown, Form, MenuProps, message, Modal, Space, Spin } from 'antd';
import { CommonBaseContent } from '../Layouts/CommonBackContent';

import Link from 'next/link';
import { CloseCircleOutlined, ExportOutlined, PlusCircleOutlined, RightOutlined } from '@ant-design/icons';

import { InsAccountModel, InsProfileModel, UnAddAccountModel } from '@/types/home';
import dynamic from 'next/dynamic';
import { extractInstagramUsername, isInstagramProfileURL } from '@/shared/utils';
import { addInsMonitor, fetchInsMonitorList, fetchInsMonitorSearch } from '@/lib/service';
import { useRouter } from 'next/router';
import UserDashboard from '../Dashboard/UserDashboard';
import { useUserProvider } from '@/context/UserProvider';
import { debounce, forEach } from 'lodash-es';
import classNames from 'classnames';
import { FacebookShareButton, TwitterShareButton } from 'react-share';
import UnlockModalContent from '../Common/UnlockModalContent';
import CommonSimpleModal from '../Common/CommonSimpleModal';

const SocialDashboard = dynamic(() => import('./SocialDashboard'), { ssr: true });
const BottomAddAccount = dynamic(() => import('./BottomAddAccount'), { ssr: true });
const TrendingAccounts = dynamic(() => import('./TrendingAccounts'), { ssr: true });
const SocialProfileCard = dynamic(() => import('./SocialProfile'), { ssr: true });

const StartInfo: React.FC<any> = ({ data }) => {
  const { t } = useTranslation('common');
  const [dataInfo, _] = useState<InsProfileModel>(data?.StarInfo?.data);

  const bottomRef = useRef<HTMLDivElement | null>(null);
  const mainContentRef = useRef<HTMLDivElement | null>(null);
  const [isBottomVisible, setBottomVisible] = useState(false);
  const [mainContentVisible, setMainContentVisible] = useState(false);
  const [openUnLock, setOpenUnLock] = useState(false);

  const [open, toggle] = useState(false);
  const [hasAccount, setHasAccount] = useState(false);
  const { accountList, onInitAccountList, isLogin, onChangedLoginView, onOpenLoginModal, isLoginLoading } =
    useUserProvider();
  const router = useRouter();
  const { type } = router.query;
  const [loading, setLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const [accountModel, setAccountModel] = useState<InsAccountModel>();
  const [showPlan, setShowPlan] = useState<string>(undefined);
  const [openPrivateAccount, setOpenPrivateAccount] = useState<any>(null);
  const [currentUnAddAccount, setCurrentUnAddAccount] = useState<UnAddAccountModel>(null);
  const [isBtnAble, setIsBtnAble] = useState(true);
  const [searchValue, setSearchValue] = useState('');

  const [currentUser, setCurrentUser] = useState('');
  const [userData, setUserData] = useState<InsAccountModel[]>([]);
  const [inputOpen, setInputOpen] = useState(false);
  const autoRef = useRef(null);
  const currentHref = useMemo(
    () => process.env.NEXT_APP_WEB_HOST + '/instagram/' + dataInfo?.media_name,
    [dataInfo?.media_name]
  );

  useEffect(() => {
    if (type === 'refresh' && window !== undefined) {
      const url = new URL(window.location.href);
      url.searchParams.delete('type');
      window.location.href = url.toString();
    }
  }, [type, data]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setBottomVisible(entry.isIntersecting);
      },
      { threshold: 0.2 }
    );

    if (bottomRef.current) {
      observer.observe(bottomRef.current);
    }

    return () => {
      if (bottomRef.current) {
        observer.unobserve(bottomRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setMainContentVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    if (mainContentRef.current) {
      observer.observe(mainContentRef.current);
    }

    return () => {
      if (mainContentRef.current) {
        observer.unobserve(mainContentRef.current);
      }
    };
  }, []);

  // start
  const items: MenuProps['items'] = [
    {
      key: '1',
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
          <span>{t('Share')}:</span>

          <FacebookShareButton url={currentHref}>
            <Image src={'/images/starInfo/facebook-icon.webp'} alt={'facebook'} width={30} height={30} />
          </FacebookShareButton>

          <TwitterShareButton title={''} url={currentHref}>
            <Image src={'/images/starInfo/x-icon.webp'} alt={'twitter'} width={30} height={30} />
          </TwitterShareButton>
        </div>
      ),
    },
  ];

  const onAddAccount = (value?: string) => {
    setSearchValue(value?.length > 0 ? value : dataInfo?.media_name);
    setHasAccount(false);
    toggle(true);
  };

  const tooltip = () => {
    return (
      <div
        style={{
          overflow: 'hidden',
          border: '1px solid var(--app-text-30-color)',
          borderRadius: '10px',
        }}
      >
        <Image
          src={'/images/report/example-icon.webp'}
          width={572}
          height={212}
          alt={'icon'}
          style={{ width: '100%', objectFit: 'contain' }}
        />
      </div>
    );
  };

  const debounceFetcher = (value: string) => {
    setLoading(true);
    setIsBtnAble(false);
    fetchInsMonitorSearch(value, router.locale)
      .then((res) => {
        setLoading(false);
        setIsBtnAble(true);
        if (res.code == 0 && res.data?.length > 0) {
          setInputOpen(true);
          autoRef.current?.focus();

          let tempData: InsAccountModel;
          forEach(res.data, (item) => {
            if (item.media_name === value) {
              tempData = item;
            }
          });
          if (tempData !== undefined) {
            setUserData([]);
            if (tempData?.is_private === 2) {
              showInvalidNameAlert();
              return;
            } else {
              setHasAccount(true);
              setAccountModel(tempData);
            }
          } else {
            setUserData(res.data);
          }
        } else if (res?.code === 10001) {
          showInvalidNameAlert();
          return;
        } else {
          message.error(res.message);
        }
      })
      .catch((error) => {
        setLoading(false);
        message.error(error.message);
        setIsBtnAble(true);
      });
  };

  const fetchList = async () => {
    setInitLoading(true);
    const monitorList = await fetchInsMonitorList();
    if (monitorList.code === 0) {
      onInitAccountList(monitorList.data);
    } else if (monitorList.code === 40001) {
      router.replace('/');
    } else {
      message.error(monitorList.message);
    }
    setInitLoading(false);
  };

  // 搜索
  const onSearch = useCallback(
    debounce((value: string) => {
      if (!value || isInstagramProfileURL(value)) {
        setUserData([]);
        return;
      }
      autoRef.current?.blur();
      setUserData([]);
      debounceFetcher(value);
    }, 500),
    []
  );

  // 清除内容
  const onClear = () => {
    setCurrentUser('');
    setAccountModel(undefined);
    setUserData([]);
  };

  // 选择user
  const onSelect = (val: string) => {
    setInputOpen(false);
    setHasAccount(true);
    if (userData.length === 1) {
      if (userData[0]?.is_private === 2) {
        setHasAccount(false);
        showInvalidNameAlert();
        return;
      }
      setCurrentUser(val);
      onFinish({ accountName: val });
      return;
    }
    setCurrentUser(val);
    setUserData(userData.filter((user) => user.media_name === val));

    const tempData = userData.find((user) => user.media_name === val);
    if (tempData?.is_private === 2) {
      setHasAccount(false);
      showInvalidNameAlert();
      return;
    } else {
      setAccountModel(tempData);
    }
  };

  const showInvalidNameAlert = () => {
    setOpenPrivateAccount({
      title: t('Invalid Instagram Username'),
      desc: t('The username you entered is not valid. Please check the username and try again.'),
      btnTitle: t('TRYAGAIN'),
    });
    toggle(false);
  };

  const accountNameSearchRequest = (accountName?: string) => {
    setLoading(true);
    setIsBtnAble(false);
    fetchInsMonitorSearch(accountName, router.locale)
      .then((res) => {
        setLoading(false);

        setTimeout(() => {
          setIsBtnAble(true);
        }, 1500);

        if (res.code == 0 && res.data?.length > 0) {
          const data = res.data.filter((item) => item.media_name === accountName);
          if (!data.length || res.data[0]?.is_private === 2) {
            showInvalidNameAlert();
            return;
          }

          setAccountModel(res.data[0]);
          setHasAccount(true);
          toggle(true);
        } else if (res?.code === 20001) {
          setOpenPrivateAccount({
            title: t('OopsPrivateProfileDetected'),
            desc: (
              <>
                <div style={{ marginBottom: '24px' }}>{t('ThisInstagramAccountIsPrivate_Desc')}</div>
                <UserDashboard {...accountModel} />
              </>
            ),
            btnTitle: t('TRYAGAIN'),
          });
          toggle(false);
          return;
        } else if (res?.code === 20002) {
          showInvalidNameAlert();
          return;
        } else {
          message.error(res.message);
        }
      })
      .catch((error) => {
        setLoading(false);
        message.error(error.message);
        setIsBtnAble(true);
      });
  };

  // 提交搜索
  const onFinish = ({ accountName }: { accountName: string }) => {
    if (!hasAccount) {
      if (isInstagramProfileURL(accountName)) {
        // ga_logEvent(EventName.dashboard_Check);
        const username = extractInstagramUsername(accountName);
        // console.log(username);

        accountNameSearchRequest(username);
      } else {
        onSearch(accountName);
      }
      return;
    }

    if (accountModel?.is_private !== 0) {
      setAccountModel(null);
      setHasAccount(false);
      return;
    }

    // 正常账户
    if (hasAccount) {
      if (isLogin) {
        if (currentUnAddAccount === null) {
          setOpenUnLock(true);

          // setShowPlan(
          //   t(
          //     `Unlock a slot to start tracking. Gain deeper insights into their likes, follows, suspected accounts on other platforms, AI insights, and more.`,
          //   ),
          // );
          toggle(false);
          return;
        }

        setLoading(true);
        addInsMonitor(
          accountModel.media_name,
          accountModel.profile_pic_url,
          accountModel.media_url,
          currentUnAddAccount?.subs_id,
          router.locale
        )
          .then((res) => {
            setLoading(false);
            if (res.code === 0) {
              fetchList();
              toggle(false);
              setCurrentUnAddAccount(null);
            } else if (res?.code === 20000) {
              setShowPlan(res?.message);
              toggle(false);
              return;
            } else {
              message.error(res.message);
            }
          })
          .catch((error) => {
            setLoading(false);
            message.error(error.message);
          });
      } else {
        onChangedLoginView(false);
        onOpenLoginModal(true);
      }
    }
  };

  const modalContent = () => {
    return (
      <Modal
        className={style.modalContainer}
        open={open}
        onCancel={() => {
          toggle(false);
          onClear();
        }}
        destroyOnHidden={true}
      >
        <Form
          name="normal_edit_form"
          layout="vertical"
          className={style.formContent}
          initialValues={{ accountName: searchValue }}
          onFinish={onFinish}
          style={{ padding: '20px 24px' }}
          validateTrigger="onSubmit"
        >
          <div className={style.titleContainer}>
            {accountModel?.is_private ? t('OopsPrivateProfileDetected') : t('ADD new account')}
          </div>
          <Form.Item>
            <div className={style.desc}>
              {hasAccount ? (
                accountModel?.is_private ? (
                  t('ThisInstagramAccountIsPrivate_Desc')
                ) : (
                  t('AddToYourTrackingListToSeeTheirLikes_Desc')
                )
              ) : (
                <div>
                  <p style={{ color: '#333', marginBottom: '8px' }}>
                    {'No one will ever know. Get the answers you need:'}
                  </p>
                  <p>{'🔗 See their closest connections'}</p>
                  <p>{'👁 Track new follows and unfollows'}</p>
                  <p>{'🔍 Access in-depth activity reports'}</p>
                </div>
              )}
            </div>
          </Form.Item>
          {!hasAccount && <Form.Item>{tooltip()}</Form.Item>}
          {hasAccount ? (
            <Form.Item className="no-asterisk">
              <UserDashboard {...accountModel} />
            </Form.Item>
          ) : (
            <Form.Item
              name="accountName"
              rules={[{ required: true, message: t('PleaseInputAnAccount') }]}
              className="no-asterisk"
            >
              <AutoComplete
                ref={autoRef}
                open={inputOpen}
                options={(userData || []).map((d) => ({
                  value: d.media_name,
                  label: (
                    <Space>
                      <Avatar src={d.profile_pic_url} /> {d.media_name}
                    </Space>
                  ),
                }))}
                style={{ pointerEvents: loading ? 'none' : 'auto' }}
                onClear={onClear}
                onSelect={onSelect}
                onBlur={() => setInputOpen(false)}
                onClick={() => {
                  if (!inputOpen) setInputOpen(true);
                }}
                className={style.inputItem}
                placeholder={t('EnterTheAccountUsernameAsInTheExampleAbove')}
                allowClear={{ clearIcon: <CloseCircleOutlined /> }}
                notFoundContent={loading ? <Spin size="small" /> : null}
              />
            </Form.Item>
          )}

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              className={style.bottomButton}
              style={{ background: isBtnAble ? '' : '#999' }}
              loading={loading || isLoginLoading}
            >
              {hasAccount ? (accountModel?.is_private ? t('TRYAGAIN') : t('ADD ACCOUNT')) : t('CHECK')}
            </Button>
            <p style={{ color: 'var(--app-text-30-color)', marginTop: '10px' }}>
              {t('Note: Only public accounts can be tracked.')}
            </p>
          </Form.Item>
        </Form>
      </Modal>
    );
  };

  const planModalContent = () => {
    return (
      <Modal
        className={style.plamModalContainer}
        styles={{ body: { padding: '0px' } }}
        open={showPlan != undefined}
        onCancel={() => {
          setShowPlan(undefined);
        }}
        destroyOnHidden={true}
        footer={null}
      >
        <div
          // name="normal_edit_form"
          // layout="vertical"
          className={style.formContent}
          style={{
            padding: '20px 24px 70px 20px',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundSize: '100% 100%',
            // backgroundImage: `url('/images/plan/plan_alert_bg.webp')`,
            backgroundImage: `url('https://res-front.pumpsoul.com/ins-prod/image/plan_alert_bg.webp')`,
            borderRadius: '10px',
          }}
        >
          <div className={style.titleContainer}>{t('One Step Away from Full Access')}</div>
          <div className={style.desc} style={{ marginBottom: '60px' }}>
            {showPlan}
          </div>

          <Button
            className={style.bottomButton}
            style={{
              color: `#FFF`,
              background: `linear-gradient( 180deg, #725CFF 0%, rgba(141,131,255,0.8) 100%)`,
            }}
            onClick={() => {
              router.push(
                `/plan?media_name=${accountModel.media_name}&avatar=${accountModel.profile_pic_url}&media_url=${accountModel.media_url}`
              );
              setShowPlan(undefined);
            }}
          >
            {t('Unlock Now')}
          </Button>
        </div>
      </Modal>
    );
  };

  const getTitle = useMemo(() => {
    return `@${dataInfo?.media_name} ${t('Instagram Follower Counter and Stats')}`;
  }, [dataInfo?.media_name]);

  const getContent = useCallback(() => {
    return (
      <CommonBaseContent style={{ minHeight: '100vh' }}>
        {/* 面包屑 */}
        <Breadcrumb
          style={{ paddingTop: '20px' }}
          separator={<RightOutlined style={{ fontSize: '12px' }} />}
          className={style.breadcrumb}
          items={[
            {
              title: <Link href={'/'}>{t('Home')}</Link>,
            },
            {
              title: <Link href="/top-instagram">{t('Celebrity Influencers')}</Link>,
            },
            {
              title: <span style={{ color: 'var(--app-primary-color)' }}>{getTitle}</span>,
            },
          ]}
        />

        <h1 style={{ display: 'none', width: 0, height: 0 }}>{getTitle}</h1>

        <div className={style.headerTitleInfo}>{getTitle}</div>

        <div className={style.mainContainer} ref={mainContentRef}>
          <div className={style.leftContainer}>
            <SocialProfileCard
              data={dataInfo}
              bottomVisible={!mainContentVisible && isBottomVisible}
              onAddAccount={onAddAccount}
            />
          </div>
          <div className={style.rightContainer}>
            <SocialDashboard
              dataInfo={dataInfo}
              bottomVisible={!mainContentVisible && isBottomVisible}
              onAddAccount={onAddAccount}
            />
          </div>
        </div>

        <div ref={bottomRef}>
          <TrendingAccounts dataList={data?.listData} />
          <BottomAddAccount onAddAccount={onAddAccount} />
        </div>

        <div className={style.fixedBottomBar}>
          <Dropdown menu={{ items }} placement="topLeft" arrow>
            <button className={style.shareButton} aria-label="Share">
              <ExportOutlined style={{ fontSize: '15px' }} />
            </button>
          </Dropdown>

          <button
            className={classNames([style.trackButton, data?.has_added && style.disabled])}
            onClick={() => onAddAccount && onAddAccount()}
          >
            {
              <>
                <PlusCircleOutlined /> {t('Add Account').toLocaleUpperCase()}
              </>
            }
          </button>
        </div>
      </CommonBaseContent>
    );
  }, [data, isBottomVisible, mainContentVisible]);

  return (
    <>
      {getContent()}
      {open && modalContent()}
      {planModalContent()}
      {openUnLock && (
        <UnlockModalContent
          logo={accountModel?.profile_pic_url}
          name={accountModel?.media_name}
          open={openUnLock}
          onClose={(isOk?: boolean) => {
            if (isOk) {
              router.push(
                `/plan?media_name=${accountModel.media_name}&avatar=${accountModel.profile_pic_url}&media_url=${accountModel.media_url}`
              );
            }
            setOpenUnLock(false);
          }}
        />
      )}
      <CommonSimpleModal
        open={openPrivateAccount !== null}
        title={openPrivateAccount?.title}
        desc={openPrivateAccount?.desc}
        btnTitle={openPrivateAccount?.btnTitle}
        closeAction={(isOk: boolean) => {
          setOpenPrivateAccount(null);
          if (isOk) {
            setHasAccount(false);
            toggle(true);
          }
        }}
      />
    </>
  );
};

export default StartInfo;
